// Expense reports (PRD §4.17, ER1–ER5, ER10–ER11; rev. 1.21), slice (a). A person builds a report
// line by line, with a receipt or a signed lost-receipt affidavit where the rule needs one, and
// submits it; an admin who isn't the submitter approves or returns it; Tom is emailed to pay the
// reimbursement from QuickBooks, and his reply closes it. The API enforces every rule; this screen
// shows them.

import { useEffect, useState } from "react";
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Select,
  Subtitle2,
  Tab,
  TabList,
  Textarea,
} from "@fluentui/react-components";
import {
  CLIENT_REPORT_STATUS_LABEL,
  EXPENSE_STATUS_LABEL,
  isInternalCode,
  type ClientReportStatus,
  today,
  type ExpenseLine,
  type ExpenseReport,
  type ExpenseReportCategory,
  type ExpenseReportStatus,
  type ExpenseWarning,
  type ReceiptDraft,
} from "../../../core";
import { api, errorText, send, useApi } from "../api";
import { useCodeLabels } from "../codes";
import { DateInput } from "../DateInput";
import { cents } from "../format";
import { go, href } from "../router";
import { ErrorBar, Loading, Page, useMe, Warnings } from "../ui";
import { markRebilled, RebillTable, type RebillRow } from "./CostCoding";

interface Summary {
  id: string;
  ref: string;
  personId: string;
  personName: string;
  title: string;
  status: ExpenseReportStatus;
  lines: number;
  total: number;
  reimbursable: number;
  submittedAt: string | null;
  decidedAt: string | null;
  payment: ExpenseReport["payment"] | null;
}
interface ListResponse {
  enabled: boolean;
  me: { personId: string; name: string; cardHolder: boolean } | null;
  settings: { categories: ExpenseReportCategory[]; mileageRates: { from: string; rate: number }[]; companyCardHolders: string[] };
  settingsEtag: string | null;
  mine: Summary[];
  approvals: Summary[];
  all: Summary[];
}
interface ReportResponse {
  report: ExpenseReport & { _etag: string };
  personName: string;
  total: number;
  reimbursable: number;
  receiptRequired: string[];
  missing: string[];
  warnings: ExpenseWarning[];
  can: { edit: boolean; submit: boolean; withdraw: boolean; decide: boolean; recordPayment: boolean };
  cardHolder: boolean;
}

const STATUS_COLOR: Record<ExpenseReportStatus, "informative" | "warning" | "success" | "subtle" | "danger" | "brand"> = {
  draft: "subtle",
  submitted: "informative",
  returned: "warning",
  approved: "brand",
  reimbursed: "success",
  closed: "success",
};

export function Expenses({ id }: { id?: string }) {
  const me = useMe();
  const data = useApi<ListResponse>("/expenses");
  const d = data.data;
  if (!d) return data.loading ? <Loading /> : <ErrorBar message={data.error} />;
  if (!d.enabled)
    return (
      <Page title="Expense reports">
        <Warnings intent="info" items={["Expense reports aren't switched on yet: their receipt storage is being set up."]} />
      </Page>
    );
  if (id) return <ReportView key={id} id={id} categories={d.settings.categories} onChanged={data.reload} />;
  return <ExpenseHome d={d} canSettings={me.can("people.edit")} onChanged={data.reload} />;
}

function ExpenseHome({ d, canSettings, onChanged }: { d: ListResponse; canSettings: boolean; onChanged: () => void }) {
  const tabs = [
    ...(d.me ? [["mine", "My reports"]] : []),
    ...(d.approvals.length || useMe().can("expenses.approve") ? [["approvals", `To approve${d.approvals.length ? ` (${d.approvals.length})` : ""}`]] : []),
    ...(useMe().can("expenses.read") ? [["all", "All reports"], ["billing", "Client billing"]] : []),
    ...(useMe().can("expenses.read") && useMe().can("export") ? [["export", "Monthly export"]] : []),
    ...(canSettings ? [["settings", "Settings"]] : []),
  ] as [string, string][];
  const [tab, setTab] = useState(tabs[0]?.[0] ?? "mine");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function create() {
    setBusy(true);
    setError(undefined);
    try {
      const { report } = await send<{ report: { id: string } }>("POST", "/expenses", { title: `Expenses ${today().slice(0, 7)}` });
      go("expenses", report.id);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Page
      title="Expense reports"
      actions={
        d.me && (
          <Button appearance="primary" disabled={busy} onClick={() => void create()}>
            New report
          </Button>
        )
      }
    >
      <ErrorBar message={error} />
      <TabList selectedValue={tab} onTabSelect={(_, x) => setTab(String(x.value))}>
        {tabs.map(([k, label]) => (
          <Tab key={k} value={k}>
            {label}
          </Tab>
        ))}
      </TabList>
      {tab === "settings" ? (
        <ExpenseSettingsPanel d={d} onSaved={onChanged} />
      ) : tab === "billing" ? (
        <BillingPanel />
      ) : tab === "export" ? (
        <ExportPanel />
      ) : (
        <Card className="panel wide">
          {tab === "mine" && (
            <Caption1 className="muted">
              Report what you spent for 3Seven, with a receipt for anything over $20, and for every line once those without one pass $50. You're repaid by a separate
              payment, not in payroll. Billable lines are passed through to the client at cost.
            </Caption1>
          )}
          <ReportTable rows={tab === "mine" ? d.mine : tab === "approvals" ? d.approvals : d.all} showPerson={tab !== "mine"} />
        </Card>
      )}
    </Page>
  );
}

function ReportTable({ rows, showPerson }: { rows: Summary[]; showPerson: boolean }) {
  if (!rows.length) return <Body1 className="muted">No reports here.</Body1>;
  return (
    <div className="table-scroll">
      <table className="month-table" aria-label="Expense reports">
        <thead>
          <tr>
            {showPerson && <th>Person</th>}
            <th>Report</th>
            <th>Status</th>
            <th>Submitted</th>
            <th className="num">Lines</th>
            <th className="num">Total</th>
            <th className="num">To reimburse</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              {showPerson && <td>{r.personName}</td>}
              <td>
                <a href={href("expenses", r.id)}>{r.title}</a>
              </td>
              <td>
                <Badge appearance="tint" color={STATUS_COLOR[r.status]}>
                  {EXPENSE_STATUS_LABEL[r.status]}
                </Badge>
                {r.payment?.needsReview && (
                  <Badge appearance="filled" color="warning" style={{ marginLeft: 6 }}>
                    Reply to review
                  </Badge>
                )}
              </td>
              <td>{r.submittedAt ? r.submittedAt.slice(0, 10) : "—"}</td>
              <td className="num">{r.lines}</td>
              <td className="num">{cents(r.total)}</td>
              <td className="num">{cents(r.reimbursable)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- One report -----------------------------------------------------------------------------------

type Draft = Omit<Partial<ExpenseLine>, "affidavit"> & { affidavit?: ExpenseLine["affidavit"] | { sign: true } | null };

/** One receipt read and waiting for the person to confirm its line (ER13). */
interface Queued {
  receiptId: string;
  name: string;
  draft: ReceiptDraft;
}

function ReportView({ id, categories, onChanged }: { id: string; categories: ExpenseReportCategory[]; onChanged: () => void }) {
  const me = useMe();
  const canRead = !!me.features?.receiptAi;
  const data = useApi<ReportResponse>(`/expenses/${encodeURIComponent(id)}`);
  // The latest save's answer, so lines confirmed one after another each save on the newest version.
  const [local, setLocal] = useState<ReportResponse | null>(null);
  const [queue, setQueue] = useState<Queued[]>([]);
  const [queueAt, setQueueAt] = useState(0);
  const [progress, setProgress] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [deciding, setDeciding] = useState<"approve" | "return" | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [title, setTitle] = useState<string | null>(null);
  const { label: codeLabel } = useCodeLabels();
  const d = local ?? data.data;
  if (!d) return data.loading ? <Loading /> : <ErrorBar message={data.error} />;
  const r = d.report;
  const cat = new Map(categories.map((c) => [c.id, c]));
  const required = new Set(d.receiptRequired);
  const missing = new Set(d.missing);
  const warningsFor = (lineId: string) => d.warnings.filter((w) => w.lineId === lineId);

  async function act(f: () => Promise<unknown>) {
    setBusy(true);
    setError(undefined);
    try {
      await f();
      setLocal(null);
      data.reload();
      onChanged();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  async function saveLines(lines: Draft[], newTitle?: string): Promise<boolean> {
    setBusy(true);
    setError(undefined);
    try {
      const saved = await send<Omit<ReportResponse, "personName" | "can" | "cardHolder">>("PUT", `/expenses/${r.id}`, { etag: r._etag, title: newTitle ?? r.title, lines });
      setLocal({ ...d!, ...saved });
      onChanged();
      return true;
    } catch (err) {
      setError(errorText(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  const lineFrom = (q: Queued): Draft => ({
    id: crypto.randomUUID(),
    date: q.draft.date ?? today(),
    merchant: q.draft.merchant,
    amount: q.draft.amount,
    categoryId: q.draft.categoryId ?? categories.find((c) => c.active && !c.mileage)?.id,
    code: q.draft.code,
    paidBy: "personal",
    billable: false,
    receiptId: q.receiptId,
  });

  /** Several receipts at once (ER13): each is uploaded and read, then confirmed one by one. */
  async function addFromReceipts(files: File[]) {
    const accepted = files.filter((f) => f.type.startsWith("image/") || f.type === "application/pdf" || AS_IS.test(f.name) || /\.(jpe?g|png|webp|gif|bmp|avif)$/i.test(f.name));
    if (!accepted.length) return setError("None of those files is a photo or a PDF.");
    setError(undefined);
    const read: Queued[] = [];
    const failed: string[] = [];
    for (let i = 0; i < accepted.length; i++) {
      setProgress(`${canRead ? "Reading" : "Uploading"} receipt ${i + 1} of ${accepted.length}…`);
      try {
        const { receipt } = await send<{ receipt: { id: string } }>("POST", "/expenses/receipts", await receiptData(accepted[i]));
        const draft = canRead ? (await send<{ draft: ReceiptDraft }>("POST", `/expenses/receipts/${receipt.id}/read`)).draft : { filled: [], notes: [] };
        read.push({ receiptId: receipt.id, name: accepted[i].name, draft });
      } catch (err) {
        failed.push(`${accepted[i].name}: ${errorText(err)}`);
      }
    }
    setProgress(null);
    if (failed.length) setError(failed.join(" "));
    if (!read.length) return;
    setQueue(read);
    setQueueAt(0);
    setEditing(lineFrom(read[0]));
  }

  /** After a queued line is saved or skipped, the next one opens; the last one closes the walk-through. */
  function nextInQueue() {
    const next = queueAt + 1;
    if (next < queue.length) {
      setQueueAt(next);
      setEditing(lineFrom(queue[next]));
    } else {
      setQueue([]);
      setQueueAt(0);
      setEditing(null);
    }
  }
  const queued = queue.length ? queue[queueAt] : null;
  const withLine = (line: Draft) => {
    const others = r.lines.filter((l) => l.id !== line.id);
    return [...others, line].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  };

  return (
    <Page
      title={r.title}
      actions={
        <>
          <Button appearance="subtle" onClick={() => go("expenses")}>
            All reports
          </Button>
          {d.can.edit && r.status === "draft" && (
            <Button appearance="subtle" disabled={busy} onClick={() => window.confirm("Discard this draft?") && void act(async () => (await send("DELETE", `/expenses/${r.id}`), go("expenses")))}>
              Discard draft
            </Button>
          )}
          {d.can.withdraw && (
            <Button disabled={busy} onClick={() => void act(() => send("POST", `/expenses/${r.id}/withdraw`))}>
              Withdraw
            </Button>
          )}
          {d.can.submit && (
            <Button appearance="primary" disabled={busy || !r.lines.length || d.missing.length > 0} title={d.missing.length ? "Attach a receipt or sign an affidavit where one is needed" : undefined} onClick={() => void act(() => send("POST", `/expenses/${r.id}/submit`))}>
              {r.status === "returned" ? "Resubmit" : "Submit"}
            </Button>
          )}
          {d.can.decide && (
            <>
              <Button disabled={busy} onClick={() => setDeciding("return")}>
                Return
              </Button>
              <Button appearance="primary" disabled={busy} onClick={() => setDeciding("approve")}>
                Approve
              </Button>
            </>
          )}
        </>
      }
    >
      <ErrorBar message={error} />
      <div className="kpis" aria-label="This report">
        <div className="kpi">
          <span className="kpi-label">Status</span>
          <span className="kpi-value">
            <Badge appearance="tint" color={STATUS_COLOR[r.status]} size="large">
              {EXPENSE_STATUS_LABEL[r.status]}
            </Badge>
          </span>
          <span className="kpi-sub">{d.personName}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Total</span>
          <span className="kpi-value">{cents(d.total)}</span>
          <span className="kpi-sub">{r.lines.length === 1 ? "1 line" : `${r.lines.length} lines`}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">To reimburse</span>
          <span className="kpi-value">{cents(d.reimbursable)}</span>
          <span className="kpi-sub">A separate payment from QuickBooks, not payroll</span>
        </div>
      </div>

      {r.status === "returned" && r.decisionNote && <Warnings items={[`Returned by ${r.decidedBy}: ${r.decisionNote}`]} />}
      {d.can.edit && (
        <div className="toolbar">
          <Field label="Report name">
            <Input value={title ?? r.title} onChange={(_, x) => setTitle(x.value)} onBlur={() => title !== null && title.trim() && title !== r.title && void saveLines(r.lines, title)} />
          </Field>
        </div>
      )}

      <Card
        className={dragging ? "panel wide drop-target dragging" : d.can.edit ? "panel wide drop-target" : "panel wide"}
        onDragOver={(e) => {
          if (!d.can.edit) return;
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          if (!d.can.edit) return;
          e.preventDefault();
          setDragging(false);
          void addFromReceipts([...e.dataTransfer.files]);
        }}
      >
        <div className="panel-head">
          <Subtitle2>Lines</Subtitle2>
          {d.can.edit && (
            <div className="toolbar">
              <label className="fui-Button-like">
                <input
                  type="file"
                  multiple
                  accept={ACCEPT}
                  className="visually-hidden"
                  disabled={!!progress}
                  onChange={(e) => {
                    void addFromReceipts([...(e.target.files ?? [])]);
                    e.target.value = "";
                  }}
                />
                <span className="button-like primary">{canRead ? "Add from receipts" : "Add receipts"}</span>
              </label>
              {FINE_POINTER && (
                <label className="fui-Button-like" title="Every photo and PDF in a folder">
                  <input
                    type="file"
                    multiple
                    className="visually-hidden"
                    disabled={!!progress}
                    {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
                    onChange={(e) => {
                      void addFromReceipts([...(e.target.files ?? [])]);
                      e.target.value = "";
                    }}
                  />
                  <span className="button-like">Choose a folder</span>
                </label>
              )}
              <Button size="small" onClick={() => setEditing({ id: crypto.randomUUID(), date: today(), paidBy: "personal", billable: false, categoryId: categories.find((c) => c.active)?.id })}>
                Add a line by hand
              </Button>
            </div>
          )}
        </div>
        {d.can.edit && (
          <Caption1 className="muted">
            {canRead
              ? "Pick several receipts (on a phone, multi-select from photos or files), or drag them here: each is read and filled in for you to check, one at a time."
              : "Pick several receipts, or drag them here, and fill a line in for each."}
          </Caption1>
        )}
        {progress && <Loading label={progress} />}
        {!r.lines.length ? (
          <Body1 className="muted">No lines yet.</Body1>
        ) : (
          <div className="table-scroll">
            <table className="month-table" aria-label="Expense lines">
              <thead>
                <tr>
                  <th>Transaction date</th>
                  <th>Merchant</th>
                  <th>Category</th>
                  <th>Code</th>
                  <th className="num">Amount</th>
                  <th>Receipt</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {r.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.date}</td>
                    <td>
                      {l.merchant}
                      <Caption1 className="muted" block>
                        {l.purpose}
                        {l.attendees ? ` · with ${l.attendees}` : ""}
                        {l.paidBy === "company-card" ? " · company card" : ""}
                      </Caption1>
                      {warningsFor(l.id).map((w) => (
                        <Caption1 key={w.kind} block className="delta-bad">
                          {w.message}
                        </Caption1>
                      ))}
                      {l.returnNote && (
                        <Caption1 block className="delta-bad">
                          Returned: {l.returnNote}
                        </Caption1>
                      )}
                    </td>
                    <td>{cat.get(l.categoryId)?.name ?? l.categoryId}</td>
                    <td>
                      {codeLabel(l.code)}
                      {l.billable && <Caption1 className="muted"> · billable</Caption1>}
                    </td>
                    <td className="num">
                      {cents(l.amount)}
                      {l.miles ? <Caption1 className="muted" block>{`${l.miles} mi`}</Caption1> : null}
                    </td>
                    <td>
                      {l.receiptId ? (
                        <Button size="small" appearance="subtle" onClick={() => setViewing(l.receiptId!)}>
                          View receipt
                        </Button>
                      ) : l.affidavit ? (
                        <span title={l.affidavit.statement}>Affidavit signed {l.affidavit.signedAt.slice(0, 10)}</span>
                      ) : missing.has(l.id) ? (
                        <span className="delta-bad">Receipt needed</span>
                      ) : required.has(l.id) ? (
                        "Needed"
                      ) : (
                        <span className="muted">Not needed</span>
                      )}
                    </td>
                    <td>
                      {d.can.edit && (
                        <Button size="small" appearance="subtle" onClick={() => setEditing({ ...l })}>
                          Edit
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {d.can.edit && d.missing.length > 0 && (
          <Caption1 className="delta-bad">
            {d.missing.length === 1 ? "One line needs" : `${d.missing.length} lines need`} a receipt, or a signed lost-receipt affidavit, before you can submit.
          </Caption1>
        )}
      </Card>

      {r.payment && (
        <PaymentPanel report={r} canRecord={d.can.recordPayment} busy={busy} onAct={(body) => void act(() => send("POST", `/expenses/${r.id}/payment`, body))} />
      )}

      <Card className="panel wide">
        <Subtitle2>History</Subtitle2>
        <ul className="plain-list">
          {r.history.map((h, i) => (
            <li key={i}>
              <Caption1>
                {h.at.slice(0, 16).replace("T", " ")} · {h.by} · {h.action}
                {h.note ? `: ${h.note}` : ""}
              </Caption1>
            </li>
          ))}
        </ul>
      </Card>

      {editing && (
        <LineDialog
          key={String(editing.id)}
          line={editing}
          categories={categories}
          cardHolder={d.cardHolder}
          canRead={canRead}
          filled={queued?.draft.filled}
          notes={queued?.draft.notes}
          queueLabel={queued ? `Receipt ${queueAt + 1} of ${queue.length}${queued.name ? `: ${queued.name}` : ""}` : undefined}
          receiptRequired={required.has(String(editing.id)) || Number(editing.amount) > 20}
          onClose={() => (queued ? nextInQueue() : setEditing(null))}
          closeLabel={queued ? (queueAt + 1 < queue.length ? "Skip this one" : "Skip") : undefined}
          onDelete={!queued && r.lines.some((l) => l.id === editing.id) ? () => (setEditing(null), void saveLines(r.lines.filter((l) => l.id !== editing.id))) : undefined}
          onSave={async (line) => {
            if (await saveLines(withLine(line))) {
              if (queued) nextInQueue();
              else setEditing(null);
            }
          }}
        />
      )}
      {deciding && (
        <DecideDialog
          mode={deciding}
          lines={r.lines}
          reimbursable={d.reimbursable}
          onClose={() => setDeciding(null)}
          onDecide={(body) => (setDeciding(null), void act(() => send("POST", `/expenses/${r.id}/decide`, { decision: deciding, ...body })))}
        />
      )}
      {viewing && <ReceiptDialog id={viewing} onClose={() => setViewing(null)} />}
    </Page>
  );
}

function PaymentPanel({ report, canRecord, busy, onAct }: { report: ExpenseReport; canRecord: boolean; busy: boolean; onAct: (body: unknown) => void }) {
  const p = report.payment!;
  const [paidAt, setPaidAt] = useState(today());
  return (
    <Card className="panel wide">
      <Subtitle2>Reimbursement</Subtitle2>
      <Body1>
        {cents(p.total)} to repay by a separate payment from QuickBooks.{" "}
        {p.paidAt ? `Paid ${p.paidAt}${p.paidBy ? ` (${p.paidBy})` : ""}.` : p.emailedAt ? `Tom was emailed ${p.emailedAt.slice(0, 10)}.` : "The email to Tom is waiting to send."}
      </Body1>
      {p.needsReview && p.reply && <Warnings items={[`Reply from ${p.replyFrom ?? "Tom"}, which needs a person: "${p.reply.slice(0, 400)}"`]} />}
      {canRecord && report.status === "approved" && (
        <div className="toolbar">
          <Field label="Paid on">
            <DateInput type="date" value={paidAt} onChange={(_, x) => x.value && setPaidAt(x.value)} />
          </Field>
          <Button appearance="primary" disabled={busy} onClick={() => onAct({ action: "paid", paidAt })}>
            Mark paid
          </Button>
          {p.needsReview && (
            <Button disabled={busy} onClick={() => onAct({ action: "dismiss" })}>
              Clear the reply
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

// --- Dialogs ----------------------------------------------------------------------------------------

/**
 * A receipt ready to upload (ER5; Chris, 2026-10-21: every common phone photo, PDFs and email
 * attachments). A photo the browser can read is re-saved as JPEG here, which drops its location and
 * other metadata. One it can't (an iPhone HEIC on Windows or Android, a TIFF scan) and a PDF go as they
 * are: the API converts HEIC to JPEG and keeps PDFs and TIFFs whole.
 */
const ACCEPT = "image/*,.heic,.heif,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tif,.tiff,.avif,application/pdf,.pdf";
/** A mouse or trackpad: folder picking works there (desktop Chrome and Edge), not on phones. */
const FINE_POINTER = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: fine)").matches;
const AS_IS = /\.(pdf|tiff?|heic|heif)$/i;

async function receiptData(file: File): Promise<{ name: string; data: string }> {
  const asDataUrl = (f: Blob) =>
    new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = () => reject(new Error("The file couldn't be read."));
      fr.readAsDataURL(f);
    });
  const base64 = (dataUrl: string) => dataUrl.replace(/^data:[^,]*,/, "");
  if (file.size > 10 * 1024 * 1024) throw new Error("A receipt is up to 10 MB.");
  const original = await asDataUrl(file);
  // PDFs, TIFF scans and HEIC go to the API whole; so does any photo this browser can't draw.
  if (file.type === "application/pdf" || file.type === "image/tiff" || AS_IS.test(file.name)) {
    const img = /\.(heic|heif)$/i.test(file.name) || file.type.includes("hei") ? await loadImage(original).catch(() => null) : null;
    if (!img) return { name: file.name, data: base64(original) };
    return { name: file.name.replace(/\.[^.]+$/, "") + ".jpg", data: base64(await reencode(img)) };
  }
  const img = await loadImage(original).catch(() => null);
  if (!img) return { name: file.name, data: base64(original) };
  return { name: file.name.replace(/\.[^.]+$/, "") + ".jpg", data: base64(await reencode(img)) };
}

/** Loads from a data: address, which the site's security policy always allows. */
function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("unreadable"));
    i.src = dataUrl;
  });
}

async function reencode(img: HTMLImageElement): Promise<string> {
  const scale = Math.min(1, 2200 / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const g = canvas.getContext("2d")!;
  g.fillStyle = "#fff"; // a transparent PNG or GIF reads on white, not black
  g.fillRect(0, 0, canvas.width, canvas.height);
  g.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.85);
}

type Filled = ReceiptDraft["filled"][number];

function LineDialog(props: {
  line: Draft;
  categories: ExpenseReportCategory[];
  cardHolder: boolean;
  receiptRequired: boolean;
  /** AI can read an attached receipt and fill the line in (ER13). */
  canRead: boolean;
  /** Fields the AI already filled, from a receipt read before the dialog opened. */
  filled?: Filled[];
  notes?: string[];
  queueLabel?: string;
  closeLabel?: string;
  onClose: () => void;
  onSave: (line: Draft) => void;
  onDelete?: () => void;
}) {
  const [l, setL] = useState<Draft>(props.line);
  const [uploading, setUploading] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState<string>();
  const [notes, setNotes] = useState<string[]>(props.notes ?? []);
  // Marked "from the receipt: check it" until the person changes or confirms the field.
  const [filled, setFilled] = useState<Set<Filled>>(new Set(props.filled ?? []));
  const { options } = useCodeLabels();
  const cat = props.categories.find((c) => c.id === l.categoryId);
  const set = (patch: Draft) => {
    setL({ ...l, ...patch });
    if ((Object.keys(patch) as Filled[]).some((k) => filled.has(k))) setFilled(new Set([...filled].filter((k) => !(k in patch))));
  };
  const check = (field: Filled) => (filled.has(field) ? { validationState: "warning" as const, validationMessage: "Read from the receipt: check it" } : {});
  const internal = isInternalCode(String(l.code ?? ""));
  const valid = !!l.date && !!l.categoryId && !!l.merchant?.trim() && !!l.code && (cat?.mileage ? Number(l.miles) > 0 : Number(l.amount) > 0);
  const signed = !!l.affidavit && !("sign" in (l.affidavit as object));

  async function upload(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    setError(undefined);
    try {
      const { receipt } = await send<{ receipt: { id: string } }>("POST", "/expenses/receipts", await receiptData(file));
      let next: Draft = { ...l, receiptId: receipt.id };
      setL(next);
      if (props.canRead) {
        setReading(true);
        const { draft } = await send<{ draft: ReceiptDraft }>("POST", `/expenses/receipts/${receipt.id}/read`);
        // Fill only what's still empty: never overwrite what the person typed.
        const took: Filled[] = [];
        const take = <K extends Filled>(k: K, v: Draft[K] | undefined, empty: boolean) => {
          if (v !== undefined && empty) {
            next = { ...next, [k]: v };
            took.push(k);
          }
        };
        take("date", draft.date, !l.date || l.date === props.line.date);
        take("merchant", draft.merchant, !l.merchant);
        take("amount", draft.amount, l.amount === undefined);
        take("categoryId", draft.categoryId, l.categoryId === props.line.categoryId);
        take("code", draft.code, !l.code);
        setL(next);
        setFilled(new Set([...filled, ...took]));
        setNotes(draft.notes);
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setUploading(false);
      setReading(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, x) => !x.open && props.onClose()}>
      <DialogSurface className={l.receiptId ? "dialog-xwide" : "dialog-wide"}>
        <DialogBody>
          <DialogTitle>{props.queueLabel ?? (props.onDelete ? "Edit the line" : "Add a line")}</DialogTitle>
          <DialogContent className={l.receiptId ? "line-with-receipt" : "dialog-stack"}>
            {l.receiptId && (
              <div className="line-receipt">
                <ReceiptPreview id={l.receiptId} tall />
                <Button size="small" appearance="subtle" onClick={() => set({ receiptId: undefined })}>
                  Replace the receipt
                </Button>
              </div>
            )}
            <div className="dialog-stack">
            {reading && <Loading label="Reading the receipt…" />}
            {filled.size > 0 && <Warnings intent="info" items={["Filled in from the receipt where it could be read: check each marked field, and fill in the rest."]} />}
            {notes.length > 0 && <Warnings items={notes} />}
            <div className="form-grid">
              <Field
                label={cat?.mileage ? "Date driven" : "Transaction date"}
                required
                {...check("date")}
                hint={cat?.mileage ? undefined : "The date on the receipt: when it was paid, not the date of the trip or event."}
              >
                <DateInput type="date" value={l.date ?? ""} onChange={(_, x) => set({ date: x.value })} />
              </Field>
              <Field label="Category" required {...check("categoryId")}>
                <Select value={l.categoryId ?? ""} onChange={(_, x) => set({ categoryId: x.value })}>
                  {props.categories
                    .filter((c) => c.active || c.id === l.categoryId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </Select>
              </Field>
              <Field label={cat?.mileage ? "From and to" : "Merchant"} required {...check("merchant")}>
                <Input value={l.merchant ?? ""} onChange={(_, x) => set({ merchant: x.value })} />
              </Field>
              {cat?.mileage ? (
                <Field label="Miles" required hint="Priced at the year's IRS rate. No receipt needed.">
                  <Input type="number" min={0} value={l.miles === undefined ? "" : String(l.miles)} onChange={(_, x) => set({ miles: x.value === "" ? undefined : Number(x.value) })} />
                </Field>
              ) : (
                <Field label="Amount" required {...check("amount")}>
                  <Input type="number" min={0} step="0.01" contentBefore="$" value={l.amount === undefined ? "" : String(l.amount)} onChange={(_, x) => set({ amount: x.value === "" ? undefined : Number(x.value) })} />
                </Field>
              )}
              <Field
                label="Project code"
                required
                hint={filled.has("code") ? "Suggested from your time card that day." : "A client project, or 3S001–3S003 for internal spend."}
                {...check("code")}
              >
                <Select value={l.code ?? ""} onChange={(_, x) => set({ code: x.value, billable: isInternalCode(x.value) ? false : l.billable })}>
                  <option value="">Choose a code…</option>
                  {options.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Business purpose">
                <Input value={l.purpose ?? ""} onChange={(_, x) => set({ purpose: x.value })} />
              </Field>
              {cat?.meal && (
                <Field label="Who attended">
                  <Input value={l.attendees ?? ""} onChange={(_, x) => set({ attendees: x.value })} />
                </Field>
              )}
              {props.cardHolder && (
                <Field label="Paid with">
                  <Select value={l.paidBy ?? "personal"} onChange={(_, x) => set({ paidBy: x.value as ExpenseLine["paidBy"] })}>
                    <option value="personal">My own money (reimbursed)</option>
                    <option value="company-card">The company card (not reimbursed)</option>
                  </Select>
                </Field>
              )}
            </div>
            <Checkbox label="Billable to the client, at cost" checked={!!l.billable} disabled={internal || !l.code} onChange={(_, x) => set({ billable: !!x.checked })} />
            {!cat?.mileage && (
              <div className="stack">
                <Subtitle2>Receipt</Subtitle2>
                {l.receiptId ? (
                  <Body1>Receipt attached: it's shown alongside, so you can read it as you fill this in.</Body1>
                ) : (
                  <>
                    <Caption1 className="muted">
                      {props.canRead ? "Attach the receipt first and the line fills itself in from it. " : ""}
                      {props.receiptRequired ? "This line needs a receipt." : "Needed over $20, and for every line once those without one pass $50."} A photo (including iPhone HEIC), a PDF or a scan; on a phone you can take a picture or pick one. Photos lose their location data when uploaded.
                    </Caption1>
                    <input type="file" accept={ACCEPT} disabled={uploading} onChange={(e) => void upload(e.target.files?.[0])} aria-label="Upload a receipt" />
                    {uploading && <Loading label="Uploading…" />}
                    <Checkbox
                      label="I've lost the receipt: sign a lost-receipt affidavit with my sign-in"
                      checked={!!l.affidavit}
                      onChange={(_, x) => set({ affidavit: x.checked ? (signed ? l.affidavit : { sign: true }) : null })}
                    />
                    {l.affidavit && (
                      <Caption1 className="muted">
                        {signed
                          ? (l.affidavit as ExpenseLine["affidavit"])!.statement
                          : "You'll state that you incurred this expense, that the receipt was lost and can't be obtained, and that nobody else has repaid it. It's signed with your sign-in when you save."}
                      </Caption1>
                    )}
                  </>
                )}
              </div>
            )}
            <ErrorBar message={error} />
            </div>
          </DialogContent>
          <DialogActions>
            {props.onDelete && (
              <Button appearance="subtle" onClick={props.onDelete}>
                Remove the line
              </Button>
            )}
            <Button onClick={props.onClose}>{props.closeLabel ?? "Cancel"}</Button>
            <Button appearance="primary" disabled={!valid || uploading || reading} onClick={() => props.onSave({ ...l, merchant: l.merchant?.trim(), amount: cat?.mileage ? 0 : l.amount })}>
              {filled.size ? "Confirm and save" : "Save"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

function DecideDialog(props: { mode: "approve" | "return"; lines: ExpenseLine[]; reimbursable: number; onClose: () => void; onDecide: (body: { note: string; lineNotes: Record<string, string> }) => void }) {
  const [note, setNote] = useState("");
  const [lineNotes, setLineNotes] = useState<Record<string, string>>({});
  const approve = props.mode === "approve";
  const ready = approve || note.trim() || Object.values(lineNotes).some((n) => n.trim());
  return (
    <Dialog open onOpenChange={(_, x) => !x.open && props.onClose()}>
      <DialogSurface className={approve ? undefined : "dialog-wide"}>
        <DialogBody>
          <DialogTitle>{approve ? "Approve the report" : "Return the report"}</DialogTitle>
          <DialogContent className="dialog-stack">
            {approve ? (
              <Body1>
                {props.reimbursable > 0
                  ? `Tom is emailed to pay ${cents(props.reimbursable)} as a separate reimbursement from QuickBooks.`
                  : "Nothing is repaid (company card only), so the report closes now."}
              </Body1>
            ) : (
              <Body1>Say what to fix, overall or on a line. The person edits and resubmits.</Body1>
            )}
            <Field label={approve ? "Note (optional)" : "Note"}>
              <Textarea value={note} onChange={(_, x) => setNote(x.value)} />
            </Field>
            {!approve &&
              props.lines.map((l) => (
                <Field key={l.id} label={`${l.date} ${l.merchant} (${cents(l.amount)})`}>
                  <Input placeholder="What to fix on this line" value={lineNotes[l.id] ?? ""} onChange={(_, x) => setLineNotes({ ...lineNotes, [l.id]: x.value })} />
                </Field>
              ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onClose}>Cancel</Button>
            <Button appearance="primary" disabled={!ready} onClick={() => props.onDecide({ note, lineNotes })}>
              {approve ? "Approve" : "Return"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/** A receipt, fetched through the API and shown in the page: a photo, a PDF, or a TIFF to download. */
function ReceiptPreview({ id, tall = false }: { id: string; tall?: boolean }) {
  const [file, setFile] = useState<{ url: string; contentType: string; name: string } | null>(null);
  const [error, setError] = useState<string>();
  useEffect(() => {
    let url = "";
    setFile(null);
    setError(undefined);
    api<{ receipt: { contentType: string; data: string; name: string } }>(`/expenses/receipts/${encodeURIComponent(id)}`)
      .then(({ receipt }) => {
        const bytes = Uint8Array.from(atob(receipt.data), (c) => c.charCodeAt(0));
        url = URL.createObjectURL(new Blob([bytes], { type: receipt.contentType }));
        setFile({ url, contentType: receipt.contentType, name: receipt.name });
      })
      .catch((err) => setError(errorText(err)));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [id]);
  const cls = tall ? "receipt-view tall" : "receipt-view";
  return (
    <div className="receipt-preview">
      <ErrorBar message={error} />
      {!file && !error && <Loading label="Opening the receipt…" />}
      {file &&
        (file.contentType === "application/pdf" ? (
          <iframe src={file.url} title="Receipt" className={cls} />
        ) : file.contentType === "image/tiff" ? (
          <Body1>A TIFF scan can't be shown in the browser.</Body1>
        ) : (
          <img src={file.url} alt="Receipt" className={cls} />
        ))}
      {file && (
        <Caption1 className="muted">
          <a href={file.url} download={file.name}>
            Download
          </a>
          {file.contentType !== "application/pdf" && file.contentType !== "image/tiff" && (
            <>
              {" · "}
              <a href={file.url} target="_blank" rel="noreferrer">
                Open full size
              </a>
            </>
          )}
        </Caption1>
      )}
    </div>
  );
}

function ReceiptDialog({ id, onClose }: { id: string; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(_, x) => !x.open && onClose()}>
      <DialogSurface className="dialog-wide">
        <DialogBody>
          <DialogTitle>Receipt</DialogTitle>
          <DialogContent>
            <ReceiptPreview id={id} />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// --- Settings (ER10) ------------------------------------------------------------------------------------

function ExpenseSettingsPanel({ d, onSaved }: { d: ListResponse; onSaved: () => void }) {
  const people = useApi<{ people: { id: string; name: string; status: string }[] }>("/people?view=names");
  const [categories, setCategories] = useState(d.settings.categories);
  const [rates, setRates] = useState(d.settings.mileageRates.map((m) => ({ from: m.from, rate: String(m.rate) })));
  const [holders, setHolders] = useState<string[]>(d.settings.companyCardHolders);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);

  async function save() {
    setBusy(true);
    setError(undefined);
    try {
      await send("PUT", "/expenses/settings", {
        etag: d.settingsEtag,
        settings: { categories, mileageRates: rates.filter((r) => r.from && r.rate).map((r) => ({ from: r.from, rate: Number(r.rate) })), companyCardHolders: holders },
      });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="panel wide">
      <Subtitle2>Expense settings</Subtitle2>
      <Caption1 className="muted">Categories people pick from, the IRS mileage rate for each year, and who holds the company card.</Caption1>
      <table className="month-table" aria-label="Expense categories">
        <thead>
          <tr>
            <th>Category</th>
            <th>Active</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((c, i) => (
            <tr key={c.id}>
              <td>
                <Input size="small" value={c.name} onChange={(_, x) => (setCategories(categories.map((y, j) => (j === i ? { ...y, name: x.value } : y))), setSaved(false))} />
              </td>
              <td>
                <Checkbox checked={c.active} onChange={(_, x) => (setCategories(categories.map((y, j) => (j === i ? { ...y, active: !!x.checked } : y))), setSaved(false))} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Subtitle2>IRS mileage rate</Subtitle2>
      <Caption1 className="muted">
        Each rate applies from its start date until the next one, so a mid-year change is a new row. A mileage line is priced at the rate in effect on its own date.
      </Caption1>
      {rates.map((r, i) => (
        <div key={i} className="toolbar">
          <Field label="From">
            <DateInput size="small" type="date" value={r.from} onChange={(_, x) => (setRates(rates.map((y, j) => (j === i ? { ...y, from: x.value } : y))), setSaved(false))} />
          </Field>
          <Field label="$ per mile">
            <Input size="small" type="number" step="0.001" value={r.rate} onChange={(_, x) => (setRates(rates.map((y, j) => (j === i ? { ...y, rate: x.value } : y))), setSaved(false))} />
          </Field>
          <Button size="small" appearance="subtle" onClick={() => (setRates(rates.filter((_, j) => j !== i)), setSaved(false))}>
            Remove
          </Button>
        </div>
      ))}
      <Button size="small" appearance="subtle" onClick={() => setRates([...rates, { from: today(), rate: "" }])}>
        Add a rate
      </Button>
      <Subtitle2>Company card holders</Subtitle2>
      <div className="checks">
        {(people.data?.people ?? [])
          .filter((p) => p.status !== "archived")
          .map((p) => (
            <Checkbox key={p.id} label={p.name} checked={holders.includes(p.id)} onChange={(_, x) => (setHolders(x.checked ? [...holders, p.id] : holders.filter((h) => h !== p.id)), setSaved(false))} />
          ))}
      </div>
      <ErrorBar message={error} />
      <div className="toolbar">
        <Button appearance="primary" disabled={busy} onClick={() => void save()}>
          {busy ? "Saving…" : saved ? "Saved" : "Save"}
        </Button>
      </div>
    </Card>
  );
}


// --- Client billing (ER14–ER15) and the monthly export (ER9) --------------------------------------------

interface BillingLine {
  reportId: string;
  lineId: string;
  personName: string;
  client: string;
  code: string;
  date: string;
  merchant: string;
  category: string;
  purpose: string;
  amount: number;
  internal: "submitted" | "approved" | "reimbursed";
  approved: boolean;
  client_status: "unbilled" | ClientReportStatus;
  clientReport: { id: string; number: string } | null;
  age: number;
  flag: "ok" | "flagged" | "overdue";
}
interface ClientReportRow {
  id: string;
  number: string;
  clientName: string;
  codes: string[];
  invoiceNumber?: string;
  total: number;
  status: ClientReportStatus;
  issuedAt: string;
  issuedBy: string;
  invoicedAt?: string;
  paidAt?: string;
  lineCount: number;
}
interface BillingResponse {
  lines: BillingLine[];
  reports: ClientReportRow[];
  summary: { unbilled: number; flagged: number; overdue: number; waitingApproval: number };
  /** Billed-back QuickBooks costs still to rebill (FR10.4); null without books.read. */
  books: RebillRow[] | null;
}

/** Saves a file the API sent as base64. */
export function download(file: { name: string; contentType: string; data: string }) {
  const bytes = Uint8Array.from(atob(file.data), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: file.contentType }));
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const INTERNAL_LABEL = { submitted: "Submitted", approved: "Approved", reimbursed: "Repaid" } as const;
const CLIENT_LABEL = (l: BillingLine) => (l.client_status === "unbilled" ? (l.approved ? "Not yet billed" : "Waiting for approval") : `${CLIENT_REPORT_STATUS_LABEL[l.client_status as ClientReportStatus]}${l.clientReport ? ` (${l.clientReport.number})` : ""}`);

function AgeBadge({ l }: { l: BillingLine }) {
  if (l.client_status !== "unbilled" || !l.approved || l.flag === "ok") return null;
  return (
    <Badge appearance="filled" color={l.flag === "overdue" ? "danger" : "warning"} style={{ marginLeft: 6 }}>
      {l.flag === "overdue" ? `${l.age} days: overdue` : `${l.age} days: bill soon`}
    </Badge>
  );
}

/** Billable expenses and their client reports (ER14–ER15): issue, download, and record invoicing and payment. */
function BillingPanel() {
  const data = useApi<BillingResponse>("/expenses/billing");
  const [issuing, setIssuing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const d = data.data;
  if (!d) return data.loading ? <Loading /> : <ErrorBar message={data.error} />;
  const unbilled = d.lines.filter((l) => l.approved && l.client_status === "unbilled");
  const clients = [...new Set(unbilled.map((l) => l.client))].sort();

  async function act(f: () => Promise<unknown>) {
    setBusy(true);
    setError(undefined);
    try {
      await f();
      data.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  const pdf = (id: string) => act(async () => download(await api<{ name: string; contentType: string; data: string }>(`/expenses/client-reports/${id}/pdf`)));
  const status = (r: ClientReportRow, next: "invoiced" | "paid" | "withdrawn") => {
    if (next === "withdrawn" && !window.confirm(`Withdraw ${r.number}? Its lines become unbilled again.`)) return;
    const invoiceNumber = next === "invoiced" && !r.invoiceNumber ? (window.prompt("QuickBooks invoice number (optional)") ?? undefined) : undefined;
    void act(() => send("POST", `/expenses/client-reports/${r.id}/status`, { status: next, date: today(), ...(invoiceNumber ? { invoiceNumber } : {}) }));
  };

  return (
    <div className="stack">
      <ErrorBar message={error} />
      <div className="kpis" aria-label="Client billing">
        <div className="kpi">
          <span className="kpi-label">Not yet billed</span>
          <span className="kpi-value">{cents(d.summary.unbilled)}</span>
          <span className="kpi-sub">approved billable expenses, at cost</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Bill soon (45+ days)</span>
          <span className="kpi-value">{d.summary.flagged}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Overdue (over 90 days)</span>
          <span className={`kpi-value ${d.summary.overdue ? "bad" : ""}`}>{d.summary.overdue}</span>
        </div>
        <div className="kpi">
          <span className="kpi-label">Waiting for approval</span>
          <span className="kpi-value">{d.summary.waitingApproval}</span>
        </div>
      </div>

      <Card className="panel wide">
        <Subtitle2>Not yet billed, by client</Subtitle2>
        <Caption1 className="muted">Billable expenses on approved reports, passed through at cost. Issue a client expense report when you choose: bill within 90 days of the expense date.</Caption1>
        {!clients.length ? (
          <Body1 className="muted">Nothing waiting to be billed.</Body1>
        ) : (
          clients.map((client) => {
            const rows = unbilled.filter((l) => l.client === client);
            return (
              <div key={client} className="stack">
                <div className="panel-head">
                  <Subtitle2 as="h3">
                    {client || "No client on the Projects list"} · {cents(rows.reduce((t, l) => t + l.amount, 0))}
                  </Subtitle2>
                  {client && (
                    <Button size="small" appearance="primary" disabled={busy} onClick={() => setIssuing(client)}>
                      Issue a client report
                    </Button>
                  )}
                </div>
                <BillingLinesTable rows={rows} />
              </div>
            );
          })
        )}
      </Card>

      {d.books && d.books.length > 0 && (
        <Card className="panel wide">
          <Subtitle2>Billed-back costs from QuickBooks, to rebill</Subtitle2>
          <Caption1 className="muted">
            Costs coded Billed back on the QuickBooks screen. Each leaves this list when a QuickBooks invoice line for the client at the same amount appears, or when it's marked rebilled with its invoice number.
          </Caption1>
          <RebillTable rows={d.books} busy={busy} onMark={(r) => void act(() => markRebilled(r))} />
        </Card>
      )}

      <Card className="panel wide">
        <Subtitle2>Client expense reports</Subtitle2>
        {!d.reports.length ? (
          <Body1 className="muted">None issued yet.</Body1>
        ) : (
          <div className="table-scroll">
            <table className="month-table" aria-label="Client expense reports">
              <thead>
                <tr>
                  <th>Report</th>
                  <th>Client</th>
                  <th>Projects</th>
                  <th className="num">Lines</th>
                  <th className="num">Total</th>
                  <th>Status</th>
                  <th>Invoice</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.reports.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.number}
                      <Caption1 className="muted" block>
                        {r.issuedAt.slice(0, 10)}, {r.issuedBy}
                      </Caption1>
                    </td>
                    <td>{r.clientName}</td>
                    <td>{r.codes.join(", ")}</td>
                    <td className="num">{r.lineCount}</td>
                    <td className="num">{cents(r.total)}</td>
                    <td>
                      {CLIENT_REPORT_STATUS_LABEL[r.status]}
                      {r.paidAt ? ` ${r.paidAt}` : r.invoicedAt ? ` ${r.invoicedAt}` : ""}
                    </td>
                    <td>{r.invoiceNumber ?? "—"}</td>
                    <td>
                      <div className="toolbar">
                        {r.status !== "withdrawn" && (
                          <Button size="small" disabled={busy} onClick={() => void pdf(r.id)}>
                            PDF
                          </Button>
                        )}
                        {r.status === "issued" && (
                          <Button size="small" appearance="subtle" disabled={busy} onClick={() => status(r, "invoiced")}>
                            Mark invoiced
                          </Button>
                        )}
                        {(r.status === "issued" || r.status === "invoiced") && (
                          <Button size="small" appearance="subtle" disabled={busy} onClick={() => status(r, "paid")}>
                            Mark paid
                          </Button>
                        )}
                        {(r.status === "issued" || r.status === "invoiced") && (
                          <Button size="small" appearance="subtle" disabled={busy} onClick={() => status(r, "withdrawn")}>
                            Withdraw
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {issuing && (
        <IssueDialog
          client={issuing}
          codes={[...new Set(unbilled.filter((l) => l.client === issuing).map((l) => l.code))].sort()}
          onClose={() => setIssuing(null)}
          onIssued={async (id) => {
            setIssuing(null);
            data.reload();
            await pdf(id);
          }}
        />
      )}
    </div>
  );
}

function BillingLinesTable({ rows, showClient = false }: { rows: BillingLine[]; showClient?: boolean }) {
  const { label: codeLabel } = useCodeLabels();
  return (
    <div className="table-scroll">
      <table className="month-table" aria-label="Billable expenses">
        <thead>
          <tr>
            <th>Transaction date</th>
            <th>Who</th>
            <th>Merchant</th>
            <th>Project</th>
            {showClient && <th>Client</th>}
            <th className="num">Amount</th>
            <th>With the person</th>
            <th>With the client</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((l) => (
            <tr key={`${l.reportId}|${l.lineId}`}>
              <td>
                {l.date}
                <AgeBadge l={l} />
              </td>
              <td>{l.personName}</td>
              <td>
                <a href={href("expenses", l.reportId)}>{l.merchant}</a>
                <Caption1 className="muted" block>
                  {l.category} · {l.purpose}
                </Caption1>
              </td>
              <td>{codeLabel(l.code)}</td>
              {showClient && <td>{l.client}</td>}
              <td className="num">{cents(l.amount)}</td>
              <td>{INTERNAL_LABEL[l.internal]}</td>
              <td>{CLIENT_LABEL(l)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IssueDialog(props: { client: string; codes: string[]; onClose: () => void; onIssued: (id: string) => void }) {
  const [codes, setCodes] = useState<string[]>(props.codes);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  async function issue() {
    setBusy(true);
    setError(undefined);
    try {
      const { report } = await send<{ report: { id: string } }>("POST", "/expenses/client-reports", { client: props.client, codes, from: from || undefined, to: to || undefined, invoiceNumber: invoiceNumber || undefined });
      props.onIssued(report.id);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }
  return (
    <Dialog open onOpenChange={(_, x) => !x.open && props.onClose()}>
      <DialogSurface className="dialog-wide">
        <DialogBody>
          <DialogTitle>Client expense report for {props.client}</DialogTitle>
          <DialogContent className="dialog-stack">
            <Body1>Every approved, unbilled billable expense that matches goes on it, at cost, with its receipt. The PDF downloads for the QuickBooks invoice, and the lines are marked billed.</Body1>
            <Field label="Projects">
              <div className="checks">
                {props.codes.map((c) => (
                  <Checkbox key={c} label={c} checked={codes.includes(c)} onChange={(_, x) => setCodes(x.checked ? [...codes, c] : codes.filter((y) => y !== c))} />
                ))}
              </div>
            </Field>
            <div className="form-grid">
              <Field label="Expenses from (optional)">
                <DateInput type="date" value={from} onChange={(_, x) => setFrom(x.value)} />
              </Field>
              <Field label="To (optional)">
                <DateInput type="date" value={to} onChange={(_, x) => setTo(x.value)} />
              </Field>
              <Field label="QuickBooks invoice number (optional)" hint="It can be added later, when the invoice is sent.">
                <Input value={invoiceNumber} onChange={(_, x) => setInvoiceNumber(x.value)} />
              </Field>
            </div>
            <ErrorBar message={error} />
          </DialogContent>
          <DialogActions>
            <Button onClick={props.onClose}>Cancel</Button>
            <Button appearance="primary" disabled={busy || !codes.length} onClick={() => void issue()}>
              {busy ? "Issuing…" : "Issue and download"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/** Tom's monthly export (ER9): the approved lines that count in a month, as CSV, PDF, or a zip with receipts. */
function ExportPanel() {
  const [month, setMonth] = useState(today().slice(0, 7));
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  async function get(format: "csv" | "pdf" | "zip") {
    setBusy(format);
    setError(undefined);
    try {
      download(await api<{ name: string; contentType: string; data: string }>(`/expenses/export?month=${month}&format=${format}`));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }
  return (
    <Card className="panel wide">
      <Subtitle2>Monthly export</Subtitle2>
      <Caption1 className="muted">
        Approved expense lines that count in the month, for QuickBooks: a line dated in a closed month counts in the next open one. Every export is logged with who took it and when.
      </Caption1>
      <div className="toolbar">
        <Field label="Month">
          <Input type="month" value={month} onChange={(_, x) => /^\d{4}-\d{2}$/.test(x.value) && setMonth(x.value)} />
        </Field>
        <Button disabled={!!busy} onClick={() => void get("csv")}>
          {busy === "csv" ? "Preparing…" : "CSV"}
        </Button>
        <Button disabled={!!busy} onClick={() => void get("pdf")}>
          {busy === "pdf" ? "Preparing…" : "PDF with receipts"}
        </Button>
        <Button disabled={!!busy} onClick={() => void get("zip")}>
          {busy === "zip" ? "Preparing…" : "Zip: CSV and receipts"}
        </Button>
      </div>
      <ErrorBar message={error} />
    </Card>
  );
}

/** Billable expenses on a client or project page (ER15), for those who see expense reports. */
export function BillingCard({ client, code }: { client?: string; code?: string }) {
  const me = useMe();
  const q = client ? `client=${encodeURIComponent(client)}` : code ? `code=${encodeURIComponent(code)}` : "";
  const data = useApi<BillingResponse>(me.can("expenses.read") && me.features?.expenses && q ? `/expenses/billing?${q}` : null);
  const d = data.data;
  if (!d || (!d.lines.length && !d.reports.length)) return null;
  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>Billable expenses</Subtitle2>
        <a href={href("expenses")}>Client billing</a>
      </div>
      <Caption1 className="muted">
        {cents(d.summary.unbilled)} approved and not yet billed{d.summary.overdue ? `, ${d.summary.overdue} overdue (over 90 days)` : ""}
        {d.summary.flagged ? `, ${d.summary.flagged} to bill soon` : ""}. {d.reports.length} client {d.reports.length === 1 ? "report" : "reports"} issued.
      </Caption1>
      <BillingLinesTable rows={d.lines} showClient={!client} />
    </Card>
  );
}
